import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import * as Sentry from '@sentry/browser';
import { captureException } from '@sentry/react';
import { AxiosError } from 'axios';
import { BaseEmployeeDTO, EmployeeDTOStatusEnum, TenantRoleDTO, UserDTO, UserDTOMarketingConsentEnum } from 'probonio-shared-ui/api';
import { apis } from 'probonio-shared-ui/module/api';
import { isWafBlockedError, WafBlockedError } from '../error/api-errors';
import { NamedTenant } from './hooks';
import { AdminTenantAccessData, getAdminTenantAccessData } from './permissionSelectors';

export enum MeInitializationResult {
  SUCCESS = 'SUCCESS',
  // the /me API returns 403 forbidden if no user is found in the DB for the given access token
  USER_NOT_FOUND = 'USER_NOT_FOUND',
  MAINTENANCE = 'MAINTENANCE',
  // the user exists but doesn't have any active or inactive employment, i.e. cannot be assigned to any tenant
  NO_VALID_EMPLOYMENT = 'NO_VALID_EMPLOYMENT',
  // the account still needs to be unlocked by the employer after registration
  REGISTRATION_PENDING = 'REGISTRATION_PENDING',
  NEEDS_TO_COMPLETE_PROFILE = 'NEEDS_TO_COMPLETE_PROFILE',
  API_ERROR = 'API_ERROR',
  WAF_BLOCKED = 'WAF_BLOCKED',
}

export interface MeState {
  user?: UserDTO;
  employment?: BaseEmployeeDTO;
  currentTenantId?: string;
  adminTenants: NamedTenant[];
  adminTenantAccessData?: AdminTenantAccessData;
  tenantAdminRoles: Pick<TenantRoleDTO, 'tenantId' | 'tenantName'>[];
  initializationResult?: MeInitializationResult;
  marketingConsent: UserDTOMarketingConsentEnum;
  showWelcomeMessage?: boolean;
  wafCauseId?: string;
}

const initialState: MeState = {
  user: undefined,
  tenantAdminRoles: [],
  adminTenants: [],
  marketingConsent: UserDTOMarketingConsentEnum.NotSeen,
};
interface MaintenanceModeResponse {
  reason: 'MAINTENANCE_MODE';
  message: string;
  cause?: never;
}

interface WafBlockedResponse {
  message: string;
  cause: string;
  reason?: never;
}

export const loadMe = createAsyncThunk('me/loadMe', async () => {
  try {
    const user = (await apis.me.getMe()).data;
    let adminTenantAccessData: AdminTenantAccessData = {
      availableTenants: [],
      canSwitch: false,
      rootTenants: [],
    };
    try {
      adminTenantAccessData = await getAdminTenantAccessData(user);
    } catch {
      // best-effort enrichment — don't block login if admin data is unavailable
    }
    return {
      user,
      adminTenantAccessData,
    };
  } catch (err) {
    const axiosErr = err as AxiosError<MaintenanceModeResponse | WafBlockedResponse>;
    const status = axiosErr?.response?.status;
    const reason = axiosErr.response?.data?.reason;
    if (status === 403) {
      return {
        initializationResult: MeInitializationResult.USER_NOT_FOUND,
      };
    }
    if (status === 503 && reason === 'MAINTENANCE_MODE') {
      return {
        initializationResult: MeInitializationResult.MAINTENANCE,
      };
    } else if (isWafBlockedError(axiosErr)) {
      const wafData = axiosErr.response?.data as WafBlockedResponse;
      const cause = wafData?.cause ?? 'unknown';
      captureException(new WafBlockedError(cause), {
        tags: { wafCause: cause },
        fingerprint: ['waf-blocked', cause],
      });
      return {
        initializationResult: MeInitializationResult.WAF_BLOCKED,
        wafCauseId: wafData?.cause,
      };
    } else {
      throw err;
    }
  }
});

const LOCAL_STORAGE_KEY = 'selectedTenantId';

const IS_APP = !!import.meta.env.VITE_WEB_MANAGER_URL;

export const meSlice = createSlice({
  name: 'me',
  initialState,
  reducers: {
    switchTenant: (state, action: PayloadAction<{ tenantId: string; isAdmin?: boolean }>) => {
      if (IS_APP) {
        // Normal user: switch to different active employment
        const newEmployment = state.user?.employments?.find(employment => employment.tenantId === action.payload.tenantId);
        if (newEmployment) {
          state.employment = newEmployment;
          state.currentTenantId = newEmployment.tenantId;
          localStorage.setItem(LOCAL_STORAGE_KEY, newEmployment.tenantId);
        }
      } else {
        state.employment = state.user?.employments?.find(employment => employment.tenantId === action.payload.tenantId);
        state.currentTenantId = action.payload.tenantId;
        const adminTenants = state.adminTenants;
        if (adminTenants.some(adminTenant => adminTenant.id === action.payload.tenantId)) {
          // save the tenant if the user actually belongs to it (i.e. not superadmin)
          localStorage.setItem(LOCAL_STORAGE_KEY, action.payload.tenantId);
        }
      }
    },
    setMarketingConsent: (state, action: PayloadAction<UserDTOMarketingConsentEnum>) => {
      state.marketingConsent = action.payload;
    },
    setShowWelcomeMessage: (state, action: PayloadAction<boolean>) => {
      state.showWelcomeMessage = action.payload;
    },
  },
  extraReducers: builder => {
    builder.addCase(loadMe.fulfilled, (state, action) => {
      if (action.payload.initializationResult) {
        state.initializationResult = action.payload.initializationResult;
        state.wafCauseId = action.payload.wafCauseId;
        return;
      }

      // load last selected tenant from local storage
      const selectedTenantId = localStorage.getItem(LOCAL_STORAGE_KEY);
      const employments = action.payload.user?.employments
        ?.filter(employee => employee.status === EmployeeDTOStatusEnum.Active || employee.status === EmployeeDTOStatusEnum.Inactive)
        .sort(
          (a, b) =>
            // order: last selected, active, with admin role, others
            (b.tenantId === selectedTenantId ? 1 : 0) - (a.tenantId === selectedTenantId ? 1 : 0) ||
            (b.status === EmployeeDTOStatusEnum.Active ? 1 : 0) - (a.status === EmployeeDTOStatusEnum.Active ? 1 : 0),
        );
      const adminTenants = action.payload.adminTenantAccessData.availableTenants.sort(
        (a, b) => (b.id === selectedTenantId ? 1 : 0) - (a.id === selectedTenantId ? 1 : 0),
      );

      let currentTenantId: string | undefined;
      if (IS_APP) {
        currentTenantId = employments?.[0]?.tenantId;
      } else {
        currentTenantId = adminTenants?.[0]?.id;
      }

      if (
        IS_APP &&
        !currentTenantId &&
        action.payload.user?.employments?.some(employee => employee.status === EmployeeDTOStatusEnum.Registered)
      ) {
        state.initializationResult = MeInitializationResult.REGISTRATION_PENDING;
        return;
      } else if (!currentTenantId) {
        state.initializationResult = MeInitializationResult.NO_VALID_EMPLOYMENT;
        return;
      }

      if (employments?.some(employee => employee.requiredActions?.includes('UpdateProfile'))) {
        state.initializationResult = MeInitializationResult.NEEDS_TO_COMPLETE_PROFILE;
        return;
      }

      state.initializationResult = MeInitializationResult.SUCCESS;
      state.user = action.payload.user;
      state.marketingConsent = action.payload.user?.marketingConsent || UserDTOMarketingConsentEnum.NotSeen;
      state.showWelcomeMessage = action.payload.user?.showWelcomeMessage;
      state.employment = employments?.[0];
      state.currentTenantId = currentTenantId;
      state.adminTenants = adminTenants || [];
      state.adminTenantAccessData = {
        ...action.payload.adminTenantAccessData,
        availableTenants: adminTenants || [],
      };
      Sentry.setUser({ id: action.payload.user?.id, email: action.payload.user?.mail });
    });
    builder.addCase(loadMe.rejected, state => {
      state.initializationResult = MeInitializationResult.API_ERROR;
    });
  },
});

// Action creators are generated for each case reducer function
export const { switchTenant, setShowWelcomeMessage } = meSlice.actions;

export const meReducer = meSlice.reducer;

export function isTenantSelected(): boolean {
  return !!localStorage.getItem(LOCAL_STORAGE_KEY);
}
